import { useState } from 'react';
import { t, type DocumentNotesKind, type PagedRecord } from '@bms2/shared';
import { NoteComposer } from '../../components/composites/NoteComposer';
import { NoteList } from '../../components/composites/Tile';
import { useDateFormat } from '../../shell/ViewerZone';
import { RecordNoteModal, addNoteText } from './RecordNoteModal';
import { useNoteReplies } from './noteReplies';
import { noteDraftFrom, noteItemOf, type RowActions, type TileRow } from './tileItems';
import styles from './DocumentPage.module.css';

/**
 * 🔒 The notes written on a document, on the document's own page (decision
 * 0470). A record page draws them through the Internal Notes tile; a document
 * page has no tile canvas, so it draws this panel over the same rows, the same
 * composer at the top of the list (decision 0910) and the same author-only pencil.
 *
 * 🔒 Each note is the tile's own note row (T338; decision 0951, amended in batch
 * N3): the author on the left and the time in the viewer's zone with the pencil on
 * the right, ABOVE the text, then the text and the replies. The tile, a Deal row
 * and this panel therefore draw a note one way. 💰 The panel drew the author and
 * time beneath the text, the Quote's old layout, until 2026-09-26.
 *
 * 🔨 Losing a document's notes is a change to what a person can do, not to
 * where a thing is drawn. The accounting pages carried none until this panel,
 * and the Quote's move to a document page is what would have dropped them.
 *
 * 🔨 The pencil is the author's alone (decision 0301), the same rule the tile's
 * own row-action registry states; the server refuses anybody else's edit too.
 */
export interface DocumentNotesPanelProps {
  /** A record with a page, or a document kind the notes panel reaches without one, the Change Order (decision 0951). */
  recordType: PagedRecord | DocumentNotesKind;
  recordId: string;
  /** The document's own name, for the note modal's fixed chip. */
  recordLabel?: string;
  /** The note rows the page answer carries, newest first, in the shape the Notes tile draws. */
  rows: readonly TileRow[];
  /** The viewer, for the pencil that only a note's own author is offered. */
  personId?: string;
  /** The viewer's zone, so an instant reads in their own day and never the stored clock (decision 0261). */
  timeZone?: string;
  apiBaseUrl?: string;
  onChanged: () => void;
  onError?: (message: string) => void;
  /** The composer is withheld from a viewer who cannot write to this document. */
  mayAdd?: boolean;
}

export function DocumentNotesPanel({ recordType, recordId, recordLabel, rows, personId = '', timeZone = 'UTC', apiBaseUrl = '', onChanged, onError, mayAdd = true }: DocumentNotesPanelProps) {
  const dateFormat = useDateFormat();
  // 🔒 The same thread the Internal Notes tile draws beneath each note (decision 0671).
  const noteReplies = useNoteReplies(apiBaseUrl, onChanged);
  /** The note whose author opened its pencil; the window is keyed to it and unmounted when closed (decision 0301). */
  const [editing, setEditing] = useState<TileRow | null>(null);
  const close = () => setEditing(null);
  /**
   * 🔨 The pencil is the author's alone, and never on a note written in File
   * details, which that window writes (decision 0605): the tile's own rule.
   */
  const actions: RowActions = {
    ...noteReplies,
    onRowEdit: (row) => setEditing(row),
    rowEditLabel: t('note.edit'),
    rowEditWhen: (row) => Boolean(personId) && row.raw?.authorPersonId === personId && row.raw?.onFile !== true,
  };
  const items = rows.map((row) => ({
    ...noteItemOf(row, timeZone, actions, dateFormat),
    data: { 'data-document-note': row.id ?? '' },
    ...(row.id ? { editData: { 'data-document-note-edit': row.id } } : {}),
  }));
  /** 🔒 The composer writes through the note route with the window's defaults, and the page reads again (decision 0910). */
  const add = async (text: string): Promise<boolean> => {
    const refused = await addNoteText(recordType, recordId, text, apiBaseUrl);
    if (refused !== null) {
      onError?.(refused);
      return false;
    }
    onChanged();
    return true;
  };

  return (
    <section className={styles.block} data-document-notes={recordId} data-document-note-count={rows.length}>
      {/* 🔒 Headed in the one section-label style, as a record page's tile is (decision 1452). */}
      <h2 className={styles.sectionLabel}>{t('documentNotes.title')}</h2>
      {/* 🔒 The composer stands at the top of the list, above the newest note (decision 0910). */}
      {mayAdd ? <div data-document-notes-composer="true"><NoteComposer name={`note-${recordId}`} onAdd={add} /></div> : null}
      {rows.length === 0 ? <p className={styles.muted}>{t('documentNotes.none')}</p> : null}
      {/* 🔒 The tile's own list and note row, uncapped here because the panel stands in the page's flow (T338). The row keeps the panel's handles beside the tile's. */}
      {rows.length === 0 ? null : <NoteList items={items} name={`document-${recordId}`} capped={false} />}
      {/* 🔨 Keyed to the note it edits and unmounted when closed: a modal held open behind a flag reads its `initial` once and shows the first note's draft on the second. */}
      {editing ? (
        <RecordNoteModal
          key={editing.id ?? 'note'}
          open
          recordType={recordType}
          recordId={recordId}
          {...(recordLabel ? { recordLabel } : {})}
          apiBaseUrl={apiBaseUrl}
          noteId={editing.id ?? null}
          initial={noteDraftFrom(editing)}
          onCancel={close}
          onSaved={() => { close(); onChanged(); }}
          {...(onError ? { onError } : {})}
        />
      ) : null}
    </section>
  );
}
